import { camelCase, startCase } from 'lodash';
import { zonoApi } from '../api/zonoApi';
import {
  FilterRoute,
  FilterValue,
  ListFilterKey,
  ListFiltersMap,
  updateFiltersByRoute,
} from '../filter';
import {
  ConfirmPaymentTransactionRequest,
  PaymentDetailsByiIdRequest,
  PaymentDetailsByiIdResponse,
  PaymentFilterRequest,
  PaymentFilterResponse,
  PaymentsRequest,
  PaymentsResponse,
  PaymentDoctypeEnum,
  PaymentModeEnum,
  CreatePaymentTransactionRequest,
  CreatePaymentTransactionResponse,
  GetAccountsReponse,
  GetAccountsRequest,
  GenerateRazorpayLinkRequest,
  GenerateRazorpayLinkResponse,
  GetAdminAccountsRequest,
  GetAdminAccountsResponse,
} from './payment-model';
import { getQueryParams } from '@zonofi/common/functions/get-query-param';

export const paymentApi = zonoApi.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentList: builder.query<PaymentsResponse, PaymentsRequest>({
      query(params: PaymentsRequest) {
        const queryParams = getQueryParams(params);
        return {
          url: `invoiceservice/payments?${queryParams}`,
          method: 'GET',
        };
      },
    }),
    getPaymentFilter: builder.mutation<
      PaymentFilterResponse,
      PaymentFilterRequest
    >({
      query(params) {
        return {
          url: `invoiceservice/payments/filters`,
          method: 'POST',
          body: params,
        };
      },
      async onQueryStarted(
        { needCustomers, route },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          const customers: FilterValue[] = (data?.inviteIds || []).map(
            (item) => ({
              id: item?.id,
              label: item?.name,
              selected: false,
            })
          );
          const paymentType: FilterValue[] = (data?.docType || []).map(
            (item) => ({
              id: PaymentDoctypeEnum[item as keyof typeof PaymentDoctypeEnum],
              label: startCase(camelCase(item)),
              selected: false,
            })
          );
          const paymentMode: FilterValue[] = (data?.paymentMode || []).map(
            (item) => ({
              id: PaymentModeEnum[item as keyof typeof PaymentModeEnum],
              label: startCase(camelCase(item)),
              selected: false,
            })
          );

          const listFilter: Partial<ListFiltersMap> = {};
          if (needCustomers && customers.length > 0) {
            listFilter[ListFilterKey.Customers] = {
              multiSelect: true,
              values: customers,
            };
          }
          if (paymentType.length > 0) {
            listFilter[ListFilterKey.PaymentType] = {
              multiSelect: false,
              values: paymentType,
            };
          }
          if (paymentMode.length > 0) {
            listFilter[ListFilterKey.PaymentMode] = {
              multiSelect: true,
              values: paymentMode,
            };
          }

          dispatch(
            updateFiltersByRoute({
              route: route as FilterRoute,
              filter: {
                listFilter,
              },
            })
          );
        } catch (err) {
          console.log(err);
        }
      },
    }),
    getPaymentDetailsById: builder.query<
      PaymentDetailsByiIdResponse,
      PaymentDetailsByiIdRequest
    >({
      query(params) {
        return {
          url: `invoiceservice/payments/find`,
          method: 'GET',
          params,
        };
      },
    }),

    confirmPaymentTransaction: builder.mutation<
      any,
      ConfirmPaymentTransactionRequest
    >({
      query(arg) {
        return {
          url: 'invoiceservice/payments/confirm',
          method: 'POST',
          body: arg,
        };
      },
    }),
    createPaymentTransaction: builder.mutation<
      CreatePaymentTransactionResponse,
      CreatePaymentTransactionRequest
    >({
      query(arg) {
        return {
          url: `invoiceservice/payment-transactions`,
          method: 'POST',
          body: arg,
        };
      },
    }),

    getAccounts: builder.mutation<GetAccountsReponse, GetAccountsRequest>({
      query(args) {
        const { sellerWorkspaceId, workspaceId } = args?.params;
        return {
          url: `hub/orgs/api/accounts/${sellerWorkspaceId}`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body: { workspaceId },
        };
      },
    }),
    generateRazorpayLink: builder.mutation<
      GenerateRazorpayLinkResponse,
      GenerateRazorpayLinkRequest
    >({
      query(arg) {
        return {
          url: `mdm-integration/apps/razorpay/generate-payment-link/${arg.params.sellerWorkspaceId}`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
        };
      },
    }),
    getAdminAccounts: builder.mutation<
      GetAdminAccountsResponse,
      GetAdminAccountsRequest
    >({
      query(args) {
        return {
          url: `hub/orgs/api/admin/accounts/${args.params.sellerWorkspaceId}`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
  }),
  overrideExisting: true,
});
export const {
  useLazyGetPaymentListQuery,
  useGetPaymentFilterMutation,
  useLazyGetPaymentDetailsByIdQuery,
  useConfirmPaymentTransactionMutation,
  useCreatePaymentTransactionMutation,
  useGetAccountsMutation,
  useGenerateRazorpayLinkMutation,
  useGetAdminAccountsMutation,
} = paymentApi;
